import Image from 'next/image';
import Link from 'next/link';
import { LineItem } from 'shared/types/cart/LineItem';
import { RouteHelpers } from 'helpers/routerHelpers';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { cn } from 'helpers/utils/classnames';

interface Props {
  item: LineItem;
  isMiniCart?: boolean;
  returnLink?: string;
  showAvailability?: boolean;
  showDiscontinued?: boolean;
  actions?: React.ReactNode;
}

const ProductInfoBlock: React.FC<Props> = ({
  item,
  isMiniCart = false,
  returnLink,
  showAvailability = false,
  showDiscontinued = false,
  actions,
}) => {
  const productUrl = `${item._url}${RouteHelpers.encodeToURL({
    color: item.variant?.attributes?.actualColor,
    size: item.variant?.attributes?.size,
  })}`;

  const isDiscontinued = item.variant?.attributes?.isDiscontinued ?? false;
  const isValidQuantityForDiscontinued = isDiscontinued && (item.count || 0) <= (item.variant?.availableQuantity || 0);

  return (
    <div className={cn('flex', isMiniCart ? 'flex-grow' : 'flex-col sm:flex-row')}>
      <div className={cn('shrink-0', isMiniCart ? 'mr-4 w-20' : 'mx-auto mb-3 w-[120px] sm:mx-0 sm:mb-0 sm:mr-4')}>
        <Link href={productUrl} aria-label={`View details of ${item.name} image`}>
          <Image
            loader={amplienceImageLoader}
            src={item.variant?.images?.[0] || ''}
            width={120}
            height={120}
            loading="lazy"
            className="rounded-[6px]"
            alt={`A ${item.name} product`}
          />
        </Link>
      </div>
      <div
        className={cn(
          'text-sm font-normal leading-7 text-primary-dark',
          isMiniCart ? 'ml-2' : 'text-center sm:ml-2 sm:text-left',
        )}
      >
        <Link
          href={productUrl}
          className="cursor-pointer text-base font-semibold"
          aria-label={`View details of ${item.name}`}
        >
          {item.name}
        </Link>
        {returnLink && (
          <a
            href={returnLink}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector(returnLink)?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="block text-sm font-semibold text-red-500 hover:text-red-700"
          >
            Return Requested
          </a>
        )}
        <p>{item.variant?.sku}</p>
        {item.variant?.attributes?.actualColor && <p>Color: {item.variant?.attributes?.actualColor}</p>}
        {item.variant?.attributes?.filterFinish && (
          <p>
            Finish:
            <span className="ml-1">
              {[
                item.variant.attributes?.color ?? '',
                ...item.variant.attributes.filterFinish.map((f: { label: string }) => f.label),
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </p>
        )}
        {showAvailability && <p className="font-semibold">{item.variant?.availabilityMessage ?? ''}</p>}
        {showDiscontinued && isDiscontinued && (
          <p className="font-semibold text-red-500">
            {isValidQuantityForDiscontinued
              ? `Item discontinued. You may only order a maximum quantity of ${item.variant?.availableQuantity}`
              : `The item you previously added to your cart is no longer available. The current available quantity is ${item.variant?.availableQuantity}`}
          </p>
        )}
        {actions}
      </div>
    </div>
  );
};

export default ProductInfoBlock;
